import { useLocation, useNavigate } from 'react-router-dom';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { EventPropertiesMap } from 'contexts/analytics/types';

import { Card as CardComponent } from '@repo/ui';
import { CardError } from './CardError';

export const Card = <E extends keyof EventPropertiesMap = 'Clicked Button'>({
  title = '',
  id,
  className = '',
  cardBodyClassName,
  variant = 'normal',
  onClick,
  analyticsEventName = 'Clicked Button' as E,
  clickable,
  error,
  children,
}: {
  title?: React.ReactNode;
  id?: string;
  className?: string;
  cardBodyClassName?: string;
  variant?: 'normal' | 'wrapper';
  onClick?: () => void;
  analyticsEventName?: E;
  clickable?: EventPropertiesMap[E] & { href?: string; isExternal?: boolean };
  error?: Error | null;
  children?: React.ReactNode;
}) => {
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  const { pathname } = useLocation();

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
    if (clickable) {
      // Clickable actions (navigation, external links, event tracking, etc.) remains optional
      trackEvent(analyticsEventName, clickable);
      if (clickable.href) {
        if (clickable.isExternal) {
          window.open(clickable.href, '_blank');
        } else {
          navigate(clickable.href, {
            state: { from: pathname },
          });
        }
      }
    }
  };

  return (
    <CardComponent
      title={title}
      id={id}
      className={className}
      cardBodyClassName={cardBodyClassName}
      variant={variant}
      onClick={onClick || clickable ? handleClick : undefined}
    >
      {error ? <CardError /> : children}
    </CardComponent>
  );
};
